import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import { importDealNotes } from '@/utils/zohoImport';

const DealNotesImport = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [parsedData, setParsedData] = useState(null);
  const [parseStats, setParseStats] = useState(null);
  const [filterParentId, setFilterParentId] = useState('');
  const [filterRows, setFilterRows] = useState([]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      setParsedData(null);
      setParseStats(null);
      toast.success(`${selectedFile.name} carregado`);
    }
  };

  // Detecta BOM e retorna encoding correto
  const detectBOM = (arrayBuffer) => {
    const uint8Array = new Uint8Array(arrayBuffer);
    
    // UTF-8 BOM: EF BB BF
    if (uint8Array.length >= 3 && 
        uint8Array[0] === 0xEF && uint8Array[1] === 0xBB && uint8Array[2] === 0xBF) {
      console.log('✅ BOM detectado: UTF-8');
      return { encoding: 'UTF-8', bomLength: 3 };
    }
    
    // UTF-16 LE BOM: FF FE
    if (uint8Array.length >= 2 && uint8Array[0] === 0xFF && uint8Array[1] === 0xFE) {
      console.log('✅ BOM detectado: UTF-16 LE');
      return { encoding: 'UTF-16LE', bomLength: 2 };
    }
    
    // UTF-16 BE BOM: FE FF
    if (uint8Array.length >= 2 && uint8Array[0] === 0xFE && uint8Array[1] === 0xFF) {
      console.log('✅ BOM detectado: UTF-16 BE');
      return { encoding: 'UTF-16BE', bomLength: 2 };
    }
    
    console.log('ℹ️ Sem BOM detectado, usando UTF-8');
    return { encoding: 'UTF-8', bomLength: 0 };
  };

  // Parser manual de CSV para fallback - OTIMIZADO com chunking, detecção de delimitador
  const manualParseCSV = (text, delimiter = ',', showProgress = true) => {
    console.log(`🔧 Parser manual: usando delimitador "${delimiter}"`);
    const lines = [];
    let currentLine = [];
    let currentField = '';
    let inQuotes = false;
    let charCount = 0;
    const chunkSize = 50000; // Processar em chunks de 50K caracteres
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];
      charCount++;
      
      // Progress a cada chunk
      if (showProgress && charCount % chunkSize === 0) {
        const progress = Math.round((i / text.length) * 100);
        console.log(`📖 Parser manual: ${progress}% (${lines.length} linhas)...`);
      }
      
      if (inQuotes) {
        // DENTRO de aspas: preserva TUDO, incluindo quebras de linha
        if (char === '"' && nextChar === '"') {
          currentField += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          currentField += char;
        }
      } else {
        // FORA de aspas: processamento normal
        if (char === '"') {
          inQuotes = true;
        } else if (char === delimiter) {
          currentLine.push(currentField);
          currentField = '';
        } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
          if (char === '\r') i++;
          currentLine.push(currentField);
          lines.push(currentLine);
          currentLine = [];
          currentField = '';
        } else {
          currentField += char;
        }
      }
    }
    
    // Última linha
    if (currentField || currentLine.length > 0) {
      currentLine.push(currentField);
      lines.push(currentLine);
    }
    
    console.log(`✅ Parser manual: ${lines.length} linhas, ${charCount} caracteres processados`);
    
    if (lines.length === 0) return [];
    
    // Converter para objetos
    const headers = lines[0].map(h => h.trim());
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      const row = {};
      for (let j = 0; j < headers.length; j++) {
        row[headers[j]] = lines[i][j] || '';
      }
      const hasContent = Object.values(row).some(val => val && String(val).trim());
      if (hasContent) {
        data.push(row);
      }
    }
    
    console.log(`✅ Parser manual resultou em ${data.length} registros válidos (headers: ${headers.length})`);
    return data;
  };

  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      console.log('🚀 INÍCIO parseCSV:', {
        nome: file.name,
        tamanho: file.size,
        tipo: file.type
      });
      
      // Primeiro: detectar BOM e encoding
      const bomReader = new FileReader();
      bomReader.onload = (bomEvent) => {
        console.log('📖 BOM Reader completou');
        const { encoding, bomLength } = detectBOM(bomEvent.target.result);
        console.log('🔤 Encoding detectado:', encoding);
        
        // Log primeiros 500 caracteres para debug
        const textReader = new FileReader();
        textReader.onload = (e) => {
          const text = e.target.result;
          console.log('🔍 Preview do arquivo:', {
            nome: file.name,
            tamanho: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
            tipo: file.type,
            encoding: encoding,
            bomLength: bomLength,
            primeiros500chars: text.substring(0, 500),
            totalCaracteres: text.length,
            totalQuebrasLinha: (text.match(/\n/g) || []).length,
          });
        };
        textReader.readAsText(file.slice(0, 1000), encoding);
      
        console.log('🔍 Iniciando parsing (3 camadas otimizadas - SEM worker mode)...');

        // CAMADA 1: Papa Parse Otimizado (sem worker para evitar travamentos)
        let linesProcessed = 0;
        let parseTimeout;
        
        // Timeout de 30 segundos
        const timeoutPromise = new Promise((_, reject) => {
          parseTimeout = setTimeout(() => {
            reject(new Error('Papa Parse timeout após 30s'));
          }, 30000);
        });
        
        const parsePromise = new Promise((resolveP, rejectP) => {
          Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim(),
            encoding: encoding,
            delimiter: ',',
            dynamicTyping: false,
            quotes: true,
            quoteChar: '"',
            escapeChar: '"',
            comments: false,
          
          // CAMADA 2: Logging Detalhado
          step: (results, parser) => {
            linesProcessed++;
            if (linesProcessed % 500 === 0) {
              console.log(`📄 Papa Parse: ${linesProcessed} linhas...`);
            }
          },
          
          complete: (results) => {
            clearTimeout(parseTimeout);
            console.log('✅ Papa Parse COMPLETE callback chamado');
            console.log('📊 Papa Parse resultado:', {
              totalLinhas: results.data.length,
              errosDeParseamento: results.errors.length,
              tamanhoArquivo: file.size,
              metaInfo: results.meta,
              primeiraLinha: results.data[0],
              ultimaLinha: results.data[results.data.length - 1],
              ultimas10Linhas: results.data.slice(-10),
              headers: Object.keys(results.data[0] || {}),
            });
            
            // DEBUG: Se 0 linhas, log completo do resultado
            if (results.data.length === 0) {
              console.error('❌ ZERO LINHAS PARSEADAS!', {
                metaCompleto: results.meta,
                todosErros: results.errors,
                rawDataType: typeof results.data,
                rawDataLength: results.data?.length
              });
            }
            
            // VALIDAÇÃO SIMPLIFICADA: Apenas contar IDs únicos
            if (results.data.length > 0) {
              const recordIds = results.data
                .map(row => row['Record Id'])
                .filter(Boolean);
              
              console.log('🔍 Validação de IDs:', {
                totalRegistros: results.data.length,
                idsUnicos: new Set(recordIds).size,
                primeiroId: recordIds[0],
                ultimoId: recordIds[recordIds.length - 1],
              });
            }
          
          // Log TODOS os erros
          if (results.errors.length > 0) {
            console.warn(`⚠️ ${results.errors.length} erros no parseamento:`);
            results.errors.forEach((error, idx) => {
              if (idx < 50) {
                console.warn(`  Erro ${idx + 1} [linha ${error.row}]:`, {
                  tipo: error.type,
                  codigo: error.code,
                  mensagem: error.message,
                });
              }
            });
            if (results.errors.length > 50) {
              console.warn(`  ... e mais ${results.errors.length - 50} erros`);
            }
          }
          
          // ESTATÍSTICAS DETALHADAS
          const analyzeContent = (data) => {
            const stats = {
              totalLinhas: data.length,
              linhasComHTML: 0,
              linhasComBR: 0,
              linhasComMultiplasQuebras: 0,
              maiorLinhaChars: 0,
              mediaCharsNoteContent: 0,
            };
            
            let totalChars = 0;
            data.forEach(row => {
              const content = row['Note Content'] || '';
              totalChars += content.length;
              
              if (content.length > stats.maiorLinhaChars) {
                stats.maiorLinhaChars = content.length;
              }
              
              if (content.includes('<')) stats.linhasComHTML++;
              if (content.includes('<br>') || content.includes('<BR>')) stats.linhasComBR++;
              if ((content.match(/\n/g) || []).length > 1) stats.linhasComMultiplasQuebras++;
            });
            
            stats.mediaCharsNoteContent = data.length > 0 ? Math.round(totalChars / data.length) : 0;
            return stats;
          };
          
            const contentStats = analyzeContent(results.data);
            console.log('📊 Estatísticas de Conteúdo:', contentStats);
            
            // CAMADA 3: Fallback Manual se Papa Parse parecer ruim
            const needFallback = (results.data.length === 0);
            if (needFallback) {
              console.warn(`⚠️ Papa Parse retornou ${results.data.length} linhas. Ativando fallback manual...`);
              
              // Fallback SEQUENCIAL (tenta um encoding de cada vez) e dois delimitadores
              const tryManualParse = (encoding) => {
                return new Promise((resolveManual) => {
                  console.log(`🔄 Iniciando tentativa manual com ${encoding}...`);
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    try {
                      const text = e.target.result;
                      console.log(`📖 ${encoding}: ${text.length} caracteres lidos`);
                      console.log(`📄 Primeiras 200 chars:`, text.substring(0, 200));
                      
                      const manualDataComma = manualParseCSV(text, ',', true);
                      const manualDataSemi = manualParseCSV(text, ';', false);
                      const chosen = manualDataSemi.length > manualDataComma.length ? manualDataSemi : manualDataComma;
                      const manualStats = analyzeContent(chosen);
                      const headerCount = Object.keys(chosen[0] || {}).length;
                      
                      console.log(`✅ ${encoding}: comma=${manualDataComma.length} linhas, semicolon=${manualDataSemi.length} linhas, escolhidas=${chosen.length} (headers: ${headerCount})`);
                      resolveManual({ data: chosen, stats: manualStats, encoding, headerCount });
                    } catch (error) {
                      console.error(`❌ Erro no manual parse (${encoding}):`, error);
                      resolveManual({ data: [], stats: null, encoding, headerCount: 0 });
                    }
                  };
                  reader.onerror = (error) => {
                    console.error(`❌ Erro ao ler arquivo (${encoding}):`, error);
                    resolveManual({ data: [], stats: null, encoding, headerCount: 0 });
                  };
                  reader.readAsText(file, encoding);
                });
              };
              
              const encodingsToTry = [encoding, 'UTF-8', 'ISO-8859-1', 'Windows-1252'];
              const uniqueEncodings = [...new Set(encodingsToTry)];
              
              const trySequentially = async () => {
                const attempts = [];
                for (const enc of uniqueEncodings) {
                  const attempt = await tryManualParse(enc);
                  attempts.push(attempt);
                }
                // Escolher o melhor resultado manual
                const bestManual = attempts.reduce((best, cur) => {
                  if (cur.data.length > best.data.length) return cur;
                  if (cur.data.length === best.data.length && cur.headerCount > best.headerCount) return cur;
                  return best;
                }, { data: [], headerCount: 0, stats: null });
                
                // Escolher entre Papa e Manual o que tiver mais linhas
                const finalData = (bestManual.data.length > results.data.length) ? bestManual.data : results.data;
                const finalStats = (bestManual.data.length > results.data.length) ? { ...contentStats, ...bestManual.stats } : contentStats;
                
                console.log('🏁 Resultado escolhido:', {
                  papa: results.data.length,
                  manual: bestManual.data.length,
                  escolhido: finalData.length,
                });
                setParseStats(finalStats);
                resolveP(finalData);
              };
              
              trySequentially();
            } else {
              console.log(`✅ Papa Parse SUCESSO: ${results.data.length} linhas lidas`);
              setParseStats(contentStats);
              resolveP(results.data);
            }
          },
        
          error: (error) => {
            clearTimeout(parseTimeout);
            console.error('❌ Erro fatal no Papa Parse:', error);
            rejectP(error);
          },
        });
      });
      
      // Race entre Papa Parse e timeout
      Promise.race([parsePromise, timeoutPromise])
        .then(resolve)
        .catch(reject);
    };
    
    bomReader.readAsArrayBuffer(file.slice(0, 4));
    });
  };

  // Helper: mapear campos a partir de parsedData para pré-visualização
  const guessFieldMapping = (data) => {
    const fields = Object.keys(data[0] || {}).map(f => f.toLowerCase().trim());
    const byNormalized = (target, aliases=[]) => {
      const idx = fields.findIndex(f => f === target || aliases.some(a => f.includes(a)));
      return idx >= 0 ? Object.keys(data[0] || {})[idx] : null;
    };
    return {
      recordId: byNormalized('record id', ['recordid','record_id','id']),
      parentId: byNormalized('parent id.id', ['parent id','parentid','parent_id','deal id','dealid']),
      noteContent: byNormalized('note content', ['notecontent','note_content','content','notes']),
    };
  };

  const handleFilterByDealId = () => {
    if (!parsedData || parsedData.length === 0) {
      toast.error('Você precisa parsear o CSV (clicar em Iniciar Importação) antes de filtrar');
      return;
    }
    const map = guessFieldMapping(parsedData);
    if (!map.parentId) {
      toast.error('Não foi possível identificar a coluna de Parent ID (Deal)');
      return;
    }
    const term = String(filterParentId).trim().toLowerCase();
    const rows = parsedData.filter(r => String(r[map.parentId] || '').toLowerCase().includes(term));
    setFilterRows(rows.slice(0, 50));
    
    console.log(`🔍 CSV PREVIEW DEBUG:`, {
      termo_busca: filterParentId,
      linhas_encontradas: rows.length,
      campo_parent_id: map.parentId,
      exemplo_parent_id_formato: rows.length > 0 ? rows[0][map.parentId] : null,
      primeiros_5_parent_ids: rows.slice(0, 5).map(r => r[map.parentId]),
    });
    
    toast.info(`${rows.length} linhas encontradas para o Deal ID que contém "${filterParentId}"`);
  };

  // Exportar CSV parseado para análise
  const handleExportParsedCSV = () => {
    if (!parsedData || parsedData.length === 0) {
      toast.error('Nenhum dado parseado para exportar');
      return;
    }

    try {
      // Converter para CSV
      const headers = Object.keys(parsedData[0]);
      const csvContent = [
        headers.join(','),
        ...parsedData.map(row => 
          headers.map(header => {
            const value = row[header] || '';
            // Escapar valores com vírgula ou aspas
            if (value.includes(',') || value.includes('"') || value.includes('\n')) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `parsed_${file.name}`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success('CSV parseado exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar CSV parseado');
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Por favor, carregue um arquivo CSV');
      return;
    }

    setImporting(true);
    setBatchProgress({ current: 0, total: 0 });

    try {
      toast.info('Parseando CSV de notas de deals...');
      const data = await parseCSV(file);
      setParsedData(data);
      
      console.log(`📋 Dados parseados do CSV:`, {
        totalLinhas: data.length,
        primeiroRegistro: data[0],
        campos: Object.keys(data[0] || {}),
      });
      
      // Validação dos campos necessários (mais flexível - ignora case e espaços)
      const actualFields = Object.keys(data[0] || {});
      const normalizeFieldName = (name) => name.toLowerCase().trim().replace(/\s+/g, ' ');
      
      const requiredFields = [
        { original: 'Record Id', normalized: 'record id', aliases: ['recordid', 'record_id', 'id'] },
        { original: 'Parent ID.id', normalized: 'parent id.id', aliases: ['parent id', 'parentid', 'parent_id'] },
        { original: 'Note Content', normalized: 'note content', aliases: ['notecontent', 'note_content', 'content'] },
      ];
      
      const fieldMapping = {};
      const missingFields = [];
      
      requiredFields.forEach(required => {
        const found = actualFields.find(field => {
          const normalized = normalizeFieldName(field);
          return normalized === required.normalized || 
                 required.aliases.some(alias => normalized === alias || normalized.includes(alias));
        });
        
        if (found) {
          fieldMapping[required.original] = found;
          console.log(`✅ Campo mapeado: "${required.original}" → "${found}"`);
        } else {
          missingFields.push(required.original);
        }
      });
      
      if (missingFields.length > 0) {
        console.error('❌ Campos disponíveis no CSV:', actualFields);
        console.error('❌ Campos ausentes:', missingFields);
        throw new Error(`Campos ausentes no CSV: ${missingFields.join(', ')}. Campos disponíveis: ${actualFields.join(', ')}`);
      }
      
      console.log('✅ Todos os campos necessários foram encontrados:', fieldMapping);
      
      // Estatísticas pré-importação (usando fieldMapping)
      const parentIdField = fieldMapping['Parent ID.id'];
      const noteContentField = fieldMapping['Note Content'];
      
      const uniqueParentIds = new Set(data.map(row => row[parentIdField]).filter(Boolean));
      console.log(`📊 Pré-validação:`, {
        totalLinhas: data.length,
        uniqueParentIds: uniqueParentIds.size,
        linhasSemParentId: data.filter(row => !row[parentIdField]).length,
        linhasSemContent: data.filter(row => !row[noteContentField]).length,
      });
      
      toast.info(`Importando ${data.length} notas de deals (${uniqueParentIds.size} deals únicos)...`);
      
      // Normalizar dados para usar nomes de campos padrão
      const normalizedData = data.map(row => ({
        'Record Id': row[fieldMapping['Record Id']],
        'Parent ID.id': row[fieldMapping['Parent ID.id']],
        'Note Content': row[fieldMapping['Note Content']],
        // Preservar outros campos que possam existir
        ...Object.keys(row).reduce((acc, key) => {
          if (!Object.values(fieldMapping).includes(key)) {
            acc[key] = row[key];
          }
          return acc;
        }, {}),
      }));
      
      console.log('✅ Dados normalizados:', {
        total: normalizedData.length,
        exemplo: normalizedData[0],
      });
      
      // Callback de progresso
      const progressCallback = (current, total) => {
        setBatchProgress({ current, total });
      };
      
      const importResult = await importDealNotes(normalizedData, progressCallback);
      
      setResult(importResult);
      toast.success('Importação de notas de deals concluída!');
      
    } catch (error) {
      console.error('Erro na importação:', error);
      toast.error(`Erro: ${error.message}`);
    } finally {
      setImporting(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setBatchProgress({ current: 0, total: 0 });
  };

  const progressPercentage = batchProgress.total > 0 
    ? Math.round((batchProgress.current / batchProgress.total) * 100) 
    : 0;

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Importação de Notas de Deals</h1>
        <p className="text-muted-foreground">
          Importe o arquivo CSV com as notas dos deals do Zoho CRM
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Upload do Arquivo CSV</CardTitle>
          <CardDescription>
            Selecione o arquivo Notes_Deals.csv exportado do Zoho CRM
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!result ? (
            <>
              <label className="flex items-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={importing}
                />
                {file ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <div className="flex-1">
                      <p className="font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span>Carregar arquivo CSV de notas de deals</span>
                  </>
                )}
              </label>

              {importing && batchProgress.total > 0 && (
                <Alert className="border-primary">
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Processando notas em lotes...</span>
                        <span className="text-lg font-bold text-primary">
                          {batchProgress.current.toLocaleString()} / {batchProgress.total.toLocaleString()}
                        </span>
                      </div>
                      <Progress 
                        value={progressPercentage} 
                        className="h-3"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>{progressPercentage}% completo</span>
                        <span>
                          {batchProgress.total - batchProgress.current > 0 
                            ? `${(batchProgress.total - batchProgress.current).toLocaleString()} restantes` 
                            : 'Finalizando...'}
                        </span>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <Alert>
                <AlertDescription>
                  <p className="font-semibold mb-2">ℹ️ Informações importantes:</p>
                  <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                    <li>O arquivo deve estar no formato CSV exportado do Zoho CRM</li>
                    <li>As notas serão vinculadas aos deals existentes pelo Zoho Deal ID</li>
                    <li>Notas duplicadas (mesmo Zoho Note ID) serão atualizadas automaticamente</li>
                    <li>O processamento é feito em lotes de 100 registros para maior eficiência</li>
                    <li>Notas sem deal associado serão ignoradas</li>
                    <li><strong>Parser Ultra-Robusto:</strong> 3 camadas com detecção de BOM, múltiplos encodings e preservação de quebras de linha em HTML</li>
                  </ul>
                </AlertDescription>
              </Alert>

              {parsedData && parseStats && (
                <Alert className="border-blue-500">
                  <AlertDescription>
                    <p className="font-semibold mb-2">📊 Estatísticas do Parsing:</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Total de linhas: <strong>{parseStats.totalLinhas}</strong></div>
                      <div>Com HTML: <strong>{parseStats.linhasComHTML}</strong></div>
                      <div>Com &lt;br&gt;: <strong>{parseStats.linhasComBR}</strong></div>
                      <div>Quebras múltiplas: <strong>{parseStats.linhasComMultiplasQuebras}</strong></div>
                      <div>Maior linha: <strong>{parseStats.maiorLinhaChars} chars</strong></div>
                      <div>Média chars: <strong>{parseStats.mediaCharsNoteContent}</strong></div>
                    </div>
                    <Button
                      onClick={handleExportParsedCSV}
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Exportar CSV Parseado (Análise)
                    </Button>

                    <div className="mt-4 space-y-2">
                      <p className="font-semibold">🔎 Inspecionar por Deal ID (Zoho)</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Ex: 5850729000001234567"
                          value={filterParentId}
                          onChange={(e) => setFilterParentId(e.target.value)}
                        />
                        <Button size="sm" onClick={handleFilterByDealId}>Ver</Button>
                      </div>
                      {filterRows && filterRows.length > 0 && (
                        <div className="mt-2 border rounded p-2 bg-background max-h-56 overflow-auto text-xs">
                          <div className="mb-2 text-muted-foreground">Mostrando {Math.min(filterRows.length, 50)} de {filterRows.length} linhas encontradas</div>
                          <ul className="space-y-1">
                            {filterRows.slice(0, 50).map((r, idx) => (
                              <li key={idx} className="border-b last:border-b-0 pb-1">
                                <div className="flex gap-3">
                                  <span className="font-mono">Record Id: {String(r[guessFieldMapping([r]).recordId] || '')}</span>
                                  <span className="font-mono">Deal: {String(r[guessFieldMapping([r]).parentId] || '')}</span>
                                </div>
                                <div className="truncate text-muted-foreground">{String(r[guessFieldMapping([r]).noteContent] || '').slice(0, 120)}</div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handleImport}
                disabled={importing || !file}
                className="w-full"
                size="lg"
              >
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Importando notas de deals...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="mr-2 h-5 w-5" />
                    Iniciar Importação
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {/* Results */}
              <div className="border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-950 p-4 rounded">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-lg">Importação Concluída</h3>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-background rounded">
                    <div className="text-3xl font-bold text-green-600">{result.created}</div>
                    <div className="text-sm text-muted-foreground">Criadas</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded">
                    <div className="text-3xl font-bold text-blue-600">{result.updated}</div>
                    <div className="text-sm text-muted-foreground">Atualizadas</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded">
                    <div className="text-3xl font-bold text-gray-600">{result.skipped}</div>
                    <div className="text-sm text-muted-foreground">Ignoradas</div>
                  </div>
                </div>

                <div className="text-sm text-muted-foreground mb-4">
                  <p className="font-medium mb-1">Total processado: {result.created + result.updated + result.skipped} notas</p>
                  
                  {result.skipReasons && (
                    <div className="mt-3 p-3 bg-background rounded border">
                      <p className="font-semibold mb-2 text-foreground">📊 Motivos das notas ignoradas:</p>
                      <ul className="space-y-1 text-xs">
                        <li>🔴 Sem Parent ID: <strong>{result.skipReasons.noParentId}</strong></li>
                        <li>🟠 Deal não encontrado: <strong>{result.skipReasons.dealNotFound}</strong></li>
                        <li>🟡 Conteúdo vazio: <strong>{result.skipReasons.emptyContent}</strong></li>
                        <li>⚪ Dados inválidos: <strong>{result.skipReasons.invalidData}</strong></li>
                      </ul>
                      
                      {result.statistics && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="font-semibold mb-1 text-foreground">📈 Estatísticas:</p>
                          <ul className="space-y-1 text-xs">
                            <li>Total de linhas no CSV: <strong>{result.statistics.totalLinhas}</strong></li>
                            <li>Deals únicos: <strong>{result.statistics.uniqueParentIds}</strong></li>
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {result.errors && result.errors.length > 0 && (
                  <Alert className="border-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-semibold mb-2">{result.errors.length} erros encontrados:</p>
                      <div className="max-h-48 overflow-y-auto">
                        <ul className="list-disc list-inside text-xs space-y-1">
                          {result.errors.slice(0, 10).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                          {result.errors.length > 10 && (
                            <li className="font-semibold text-destructive">
                              ... e mais {result.errors.length - 10} erros
                            </li>
                          )}
                        </ul>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <Button
                onClick={handleReset}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Fazer Nova Importação
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle>Como usar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
              1
            </div>
            <div>
              <p className="font-medium">Exporte as notas de deals do Zoho CRM</p>
              <p className="text-sm text-muted-foreground">
                Vá em Notes no Zoho CRM e exporte como CSV (filtrado por "Parent Module = Deals")
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
              2
            </div>
            <div>
              <p className="font-medium">Faça upload do arquivo CSV</p>
              <p className="text-sm text-muted-foreground">
                Clique na área de upload acima e selecione o arquivo Notes_Deals.csv
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
              3
            </div>
            <div>
              <p className="font-medium">Aguarde a importação</p>
              <p className="text-sm text-muted-foreground">
                O sistema importará todas as notas, vinculando-as automaticamente aos deals correspondentes
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DealNotesImport;
